import {memo, useCallback, useEffect, useMemo} from 'react'

import {useBranch, useSyncedBooleanQueryParam} from '../../../../hooks/routes'
import {getBranchLocator} from '../../../../rest/locators'
import {getProject} from '../../../../selectors'
import {restApi} from '../../../../services/rest'
import type {ProjectId} from '../../../../types'
import {emptyArray} from '../../../../utils/empty'
import {subscribeOnProjectEvents} from '../../../../utils/subscriber'
import {CHANGE_ADDED} from '../../../../utils/subscriptionEvents'
import {UserProperties} from '../../../../utils/userProperties'
import Loader from '../../../common/Loader/Loader'
import {
  ChangeContext,
  DEFAULT_CHANGES_LIST_CONTEXT,
} from '../../../packages/Changes/Change/Change.context'
import {useChangesFilter} from '../../../packages/Changes/Changes.hooks'
import ChangesFilter from '../../../packages/Changes/ChangesFilter/ChangesFilter'
import ChangesLog from '../../../packages/Changes/ChangesLog/ChangesLog'
import {useChangeLogLocator} from '../../../packages/Changes/ChangesLog/ChangesLog.hooks'
import {getChangeLogArg} from '../../../packages/Changes/ChangesLog/ChangesLog.utils'

import styles from './ProjectChangeLogTab.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  projectId: ProjectId
}

function ProjectChangeLogTab({projectId}: Props) {
  const branch = useBranch()
  const {isFilterChanged} = useChangesFilter()

  const branchLocator = getBranchLocator(branch, true)
  const branchAndPolicyLocator = branchLocator ? `,${branchLocator}` : ''
  const baseLocator =
    branch != null
      ? `buildType:(affectedProject:${projectId})${branchAndPolicyLocator}`
      : `project:${projectId}`

  const projectInternalId = useAppSelector(state => getProject(state, projectId)?.internalId)
  const [showFiles] = useSyncedBooleanQueryParam({
    param: 'showFiles',
    userProperty: UserProperties.SHOW_ALL_CHANGES_FILES,
  })

  const changesListContextValue = useMemo(
    () => ({...DEFAULT_CHANGES_LIST_CONTEXT, showFiles}),
    [showFiles],
  )

  const locator = useChangeLogLocator({projectId, branch})

  const changeLogArg = useMemo(
    () =>
      locator != null
        ? getChangeLogArg({
            locator,
            changesOptions: {withFiles: showFiles, withUpdatePager: true},
            withGraph: true,
          })
        : null,
    [locator, showFiles],
  )

  const [fetchChangeLog, {changes, changeLogReady}] = restApi.endpoints.getChangeLog.useLazyQuery({
    refetchOnFocus: true,
    selectFromResult: ({data}) => ({
      changes: data?.changes?.result ?? emptyArray,
      changeLogReady: data != null,
    }),
  })

  const fetch = useCallback(() => {
    if (changeLogArg != null) {
      fetchChangeLog(changeLogArg)
    }
  }, [changeLogArg, fetchChangeLog])

  useEffect(() => {
    if (projectInternalId != null) {
      return subscribeOnProjectEvents(projectInternalId, [CHANGE_ADDED], fetch)
    } else {
      fetch()
      return undefined
    }
  }, [projectInternalId, fetch])

  return changeLogReady && locator != null ? (
    <div>
      {((changeLogReady && changes.length > 0) || isFilterChanged) && (
        <ChangesFilter className={styles.filter} baseLocator={baseLocator} />
      )}
      <ChangeContext.Provider value={changesListContextValue}>
        <ChangesLog locator={locator} />
      </ChangeContext.Provider>
    </div>
  ) : (
    <Loader />
  )
}

export default memo<Props>(ProjectChangeLogTab)
